/**
 * OnboardingWizard — 5-step onboarding state machine.
 *
 * Phase 4b layers on a11y/keyboard/mobile polish: progress bar exposes
 * 1-indexed aria values + per-step aria-current/aria-label, focus is moved
 * to each step's heading on transition, and step components live inside a
 * labeled <section role="form"> wrapper.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useReducedMotion } from '@/hooks/useReducedMotion'
import { WelcomeStep } from './WelcomeStep'
import { OrganizationStep } from './OrganizationStep'
import { CanaryStep } from './CanaryStep'
import { EmbedStep } from './EmbedStep'
import { DoneStep } from './DoneStep'

type WizardState = {
  orgName: string
  orgSlug: string
  orgId: string
  canaryName: string
  authorizedDomain: string
  authorizedDomainId: string
  canaryId: string
  canaryToken: string
}

type StepIndex = 0 | 1 | 2 | 3 | 4

const STEP_LABELS = ['Welcome', 'Organization', 'Canary', 'Embed', 'Done'] as const

const INITIAL_STATE: WizardState = {
  orgName: '',
  orgSlug: '',
  orgId: '',
  canaryName: '',
  authorizedDomain: '',
  authorizedDomainId: '',
  canaryId: '',
  canaryToken: '',
}

function CheckmarkIcon() {
  return (
    <svg
      className="h-4 w-4 text-white"
      viewBox="0 0 20 20"
      fill="currentColor"
      aria-hidden="true"
    >
      <path
        fillRule="evenodd"
        d="M16.704 5.29a1 1 0 010 1.42l-7.5 7.5a1 1 0 01-1.42 0l-3.5-3.5a1 1 0 011.42-1.42L8.5 12.085l6.79-6.795a1 1 0 011.414 0z"
        clipRule="evenodd"
      />
    </svg>
  )
}

type StepState = 'completed' | 'current' | 'hollow'

function stateFor(idx: number, current: number): StepState {
  if (idx < current) return 'completed'
  if (idx === current) return 'current'
  return 'hollow'
}

function circleClasses(state: StepState, pulse: boolean): string {
  const base =
    'flex h-8 w-8 items-center justify-center rounded-full transition-colors duration-300'
  if (state === 'completed') return `${base} bg-primary-600`
  if (state === 'current') {
    return `${base} bg-primary-600 ${pulse ? 'animate-stepperPulse' : ''}`.trim()
  }
  return `${base} bg-white border-2 border-gray-300`
}

function stepAriaLabel(idx: number, state: StepState): string {
  const total = STEP_LABELS.length
  const label = STEP_LABELS[idx]
  const suffix =
    state === 'current' ? '(current)' : state === 'completed' ? '(completed)' : '(upcoming)'
  return `Step ${idx + 1} of ${total}: ${label} ${suffix}`
}

function ProgressBar({ current, total }: { current: number; total: number }) {
  const reduced = useReducedMotion()
  return (
    <div
      className="w-full"
      role="progressbar"
      aria-valuemin={1}
      aria-valuemax={total}
      aria-valuenow={current + 1}
      aria-label="Onboarding progress"
    >
      <ol className="flex items-start">
        {STEP_LABELS.map((label, idx) => {
          const state = stateFor(idx, current)
          const isLast = idx === STEP_LABELS.length - 1
          const lineCompleted = idx < current
          return (
            <li
              key={label}
              className={`flex flex-col items-center ${isLast ? '' : 'flex-1'}`}
              aria-label={stepAriaLabel(idx, state)}
              {...(state === 'current' ? { 'aria-current': 'step' as const } : {})}
            >
              <div className="flex w-full items-center">
                <div
                  data-testid={`step-indicator-${idx}`}
                  data-state={state}
                  className={circleClasses(state, !reduced)}
                >
                  {state === 'completed' ? <CheckmarkIcon /> : null}
                </div>
                {isLast ? null : (
                  <div
                    data-testid={`step-line-${idx}`}
                    data-state={lineCompleted ? 'completed' : 'pending'}
                    className={[
                      'h-0.5 flex-1 mx-2 transition-colors duration-300',
                      lineCompleted
                        ? 'bg-primary-200'
                        : 'border-t-2 border-dashed border-gray-200',
                    ].join(' ')}
                  />
                )}
              </div>
              <span
                className={[
                  'hidden sm:block mt-2 text-xs',
                  state === 'current'
                    ? 'font-semibold text-primary-700'
                    : 'text-gray-500',
                ].join(' ')}
              >
                {label}
              </span>
            </li>
          )
        })}
      </ol>
    </div>
  )
}

export function OnboardingWizard() {
  const reduced = useReducedMotion()
  const [step, setStep] = useState<StepIndex>(0)
  const [state, setState] = useState<WizardState>(INITIAL_STATE)
  const [lockedSteps, setLockedSteps] = useState<ReadonlySet<number>>(new Set())
  const headingRef = useRef<HTMLHeadingElement | null>(null)

  // Move focus to the active step's heading after each transition so screen
  // reader users hear the new step title and keyboard users land in context.
  useEffect(() => {
    headingRef.current?.focus()
  }, [step])

  const goNext = useCallback(() => {
    setStep((current) => Math.min(4, current + 1) as StepIndex)
  }, [])

  const lockStep = useCallback((index: number) => {
    setLockedSteps((prev) => {
      if (prev.has(index)) return prev
      const next = new Set(prev)
      next.add(index)
      return next
    })
  }, [])

  const handleOrgCreated = useCallback(
    (org: { id: string; name: string; slug: string }) => {
      setState((prev) => ({ ...prev, orgId: org.id, orgName: org.name, orgSlug: org.slug }))
      lockStep(1)
      goNext()
    },
    [goNext, lockStep]
  )

  const handleCanaryCreated = useCallback(
    (canary: { id: string; token: string; name: string; domain: string; domainId: string }) => {
      setState((prev) => ({
        ...prev,
        canaryId: canary.id,
        canaryToken: canary.token,
        canaryName: canary.name,
        authorizedDomain: canary.domain,
        authorizedDomainId: canary.domainId,
      }))
      lockStep(2)
      goNext()
    },
    [goNext, lockStep]
  )

  // ReviewSummary updaters — called after successful PATCH on embed step
  const handleOrgNameUpdated = useCallback((name: string) => {
    setState((prev) => ({ ...prev, orgName: name }))
  }, [])

  const handleCanaryNameUpdated = useCallback((name: string) => {
    setState((prev) => ({ ...prev, canaryName: name }))
  }, [])

  const handleDomainUpdated = useCallback((domain: string) => {
    setState((prev) => ({ ...prev, authorizedDomain: domain }))
  }, [])

  const content = useMemo(() => {
    switch (step) {
      case 0:
        return <WelcomeStep onNext={goNext} headingRef={headingRef} />
      case 1:
        return <OrganizationStep onCreated={handleOrgCreated} headingRef={headingRef} />
      case 2:
        return (
          <CanaryStep
            organizationId={state.orgId}
            onCreated={handleCanaryCreated}
            headingRef={headingRef}
          />
        )
      case 3:
        return (
          <EmbedStep
            canaryToken={state.canaryToken}
            orgId={state.orgId}
            orgName={state.orgName}
            canaryId={state.canaryId}
            canaryName={state.canaryName}
            authorizedDomain={state.authorizedDomain}
            authorizedDomainId={state.authorizedDomainId}
            onOrgNameUpdated={handleOrgNameUpdated}
            onCanaryNameUpdated={handleCanaryNameUpdated}
            onDomainUpdated={handleDomainUpdated}
            onNext={goNext}
            headingRef={headingRef}
          />
        )
      case 4:
      default:
        return <DoneStep headingRef={headingRef} />
    }
  }, [step, goNext, handleOrgCreated, handleCanaryCreated, handleOrgNameUpdated, handleCanaryNameUpdated, handleDomainUpdated, state])

  // `lockedSteps` is intentionally read-only once set — it exists so future
  // "back" controls can skip over completed API steps. Referenced here so
  // the linter doesn't flag it as unused.
  void lockedSteps

  return (
    <div className="min-h-screen w-full bg-white">
      <div className="max-w-4xl mx-auto px-6 py-10">
        <ProgressBar current={step} total={STEP_LABELS.length} />
        <div
          key={step}
          data-testid="wizard-step-container"
          className={`mt-12 ${reduced ? '' : 'animate-stepEnter'}`}
        >
          {content}
        </div>
      </div>
    </div>
  )
}
